import { ComponentProps, ReactNode, useContext } from 'react';
import { OTPInput, OTPInputContext } from 'input-otp';

import { MinusIcon } from '@/assets/icons';
import { mergeClasses } from '@/utils';
import { Label } from '../Label';

interface IInputOTP {
  /** Label for the input */
  label?: string;
  /** Whether the input is required */
  required?: boolean;
  /** Error message for the input */
  error?: string;
  /** Number of OTP input slots */
  length: number;
  /** Current OTP value */
  value?: string;
  /** Callback when OTP value changes */
  onChange?: (value: string) => void;
  /** Regex pattern for allowed characters */
  pattern?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Additional class name for the wrapper */
  className?: string;
  /** Additional class name for the OTP container */
  containerClassName?: string;
  /** OTP slot components */
  children?: ReactNode;
}

const InputOTP = ({
  label,
  required,
  error,
  length,
  value,
  onChange,
  pattern,
  disabled,
  className,
  containerClassName,
  children,
}: IInputOTP) => {
  return (
    <>
      {label && (
        <Label className="!font-medium" required={required}>
          {label}
        </Label>
      )}
      <OTPInput
        data-slot="input-otp"
        maxLength={length}
        value={value}
        onChange={onChange}
        pattern={pattern}
        disabled={disabled}
        containerClassName={mergeClasses(
          'cn-input-otp flex items-center has-disabled:opacity-50',
          containerClassName,
        )}
        spellCheck={false}
        className={mergeClasses('disabled:cursor-not-allowed', className)}
      >
        {children}
      </OTPInput>
      {error && <p className="text-body-sm leading-5 text-semantic-error-base">{error}</p>}
    </>
  );
};

const InputOTPGroup = ({ className, ...props }: ComponentProps<'div'>) => {
  return (
    <div
      data-slot="input-otp-group"
      className={mergeClasses(
        'has-aria-invalid:ring-destructive/20 has-aria-invalid:border-destructive rounded-lg has-aria-invalid:ring-[3px] flex items-center gap-2',
        className,
      )}
      {...props}
    />
  );
};

const InputOTPSlot = ({
  index,
  className,
  isError,
  ...props
}: ComponentProps<'div'> & {
  index: number;
  isError?: boolean;
}) => {
  const inputOTPContext = useContext(OTPInputContext);
  const { char, isActive } = inputOTPContext?.slots[index] ?? {};

  return (
    <div
      data-slot="input-otp-slot"
      data-active={isActive}
      className={mergeClasses(
        'border border-opacity-light-20 bg-opacity-light-5 rounded-xs w-12 h-14 data-[active=true]:border-primary-base text-neutral-0 text-h6 font-bold flex items-center justify-center',
        isError && 'border-semantic-error-base',
        className,
      )}
      aria-invalid={isError}
      {...props}
    >
      {char}
    </div>
  );
};

const InputOTPSeparator = ({ ...props }: ComponentProps<'div'>) => {
  return (
    <div
      data-slot="input-otp-separator"
      className="[&_svg:not([class*='size-'])]:size-4 flex items-center"
      role="separator"
      {...props}
    >
      <MinusIcon />
    </div>
  );
};

export { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot };
